/**
 * Native action-node names — the built-in platform functions (system + library)
 * always available as node commands without a `.py` upload (PSTUDIO-2998).
 *
 * These used to be a hardcoded `standardFunctions` list. They are now consumed
 * dynamically from the backend `getActionNodeNames` GraphQL query, which returns
 * two buckets — `systemActionNodes` and `libraryActionNodes`. The command picker
 * and the PIF validator use the UNION of both (the ticket's "native action nodes
 * (system + library)"): several names that used to live in the hardcoded list
 * (e.g. ABTesting, HandleBotError) are now classified as library, so taking only
 * the system bucket would drop them and flag them missing.
 *
 * Resolution is 3-tier, most-fresh first:
 *   1. the live endpoint response (system ∪ library),
 *   2. the persisted last-known-good in localStorage (updated on every successful
 *      fetch, so an offline/failed load still gets the most recent real list — not
 *      a stale snapshot frozen in source),
 *   3. the static bootstrap default below (last resort: first-ever run with no
 *      cache and a failed fetch).
 */

/**
 * Static bootstrap default — the union of system + library action-node names as
 * of PSTUDIO-2998 (captured from the live endpoint). Last-resort safety net only:
 * the live endpoint is the source of truth and the localStorage cache carries the
 * last real list between sessions. Kept here so a brand-new client with no cache
 * and a failing fetch still renders a working command picker and validator.
 */
export const STATIC_ACTION_NODE_NAMES: readonly string[] = [
  // system
  'AsyncSysGetRAGSearchResponse',
  'AsyncSysGoogleAICompletion',
  'SysAssignVariable',
  'SysGenerateUniqueID',
  'SysJSONToGlobalVars',
  'SysLimitCounter',
  'SysMultiMatchRouting',
  'SysNodeReturn',
  'SysSetEnv',
  'SysShowMetadata',
  'SysSupervisorGetContext',
  'SysSupervisorProcessSelection',
  'SysSupervisorRouting',
  'SysVariableReset',
  'AsyncSysStartWorkflow',
  'SysSendNoticeMessage',
  'SysGetAsynchronousFunctionOutput',
  'AsyncSysCallExternalAPIWithCallback',
  'AsyncSysCallActionNodeWithCallback',
  // library
  'ABTesting',
  'Back',
  'BotToPlatform',
  'GetRichLabel',
  'Jump',
  'OAuthGetAuthToken',
  'OAuthValidateAuthCode',
  'SwitchActiveLanguage',
  'HandleBotError',
];

/** localStorage key for the last-known-good combined list. Versioned. */
export const ACTION_NODE_NAMES_CACHE_KEY = 'ps:actionNodeNames:v1';

export interface ActionNodeNames {
  libraryActionNodes: string[];
  systemActionNodes: string[];
}

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/**
 * localStorage if present and accessible, else null. Guards two failure modes:
 *   - node/SSR (vitest env = node imports this store) where `localStorage` is
 *     undefined;
 *   - a browser where storage is BLOCKED (Safari private mode, "block all
 *     cookies", 3rd-party iframe) — there even a bare `localStorage` reference
 *     THROWS `SecurityError`, which `typeof` does not prevent. Since tableStore
 *     seeds its list from this at MODULE IMPORT, an unguarded throw would
 *     white-screen the whole app; degrade to null instead.
 */
const defaultStorage = (): Storage | null => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
};

/**
 * Validate & shape the JSON scalar returned by `getActionNodeNames`.
 * Returns `null` when the payload doesn't match the contract, so callers fall
 * back rather than trust a malformed shape. Names are passed through verbatim —
 * no trimming/casing (byte-identity with the validator/compiler).
 *
 * The backend wraps the payload in an `IBotApiResponse` — the live JSON scalar is
 * `{ data: { systemActionNodes, libraryActionNodes } }`, NOT the bare buckets — so
 * unwrap a `data` envelope when present (and still accept the bare shape, in case
 * the wrapper is ever dropped).
 */
export function parseActionNodeNames(raw: unknown): ActionNodeNames | null {
  if (!raw || typeof raw !== 'object') return null;
  const outer = raw as Record<string, unknown>;
  const obj = (
    outer.data && typeof outer.data === 'object' ? outer.data : outer
  ) as Record<string, unknown>;
  if (!isStringArray(obj.systemActionNodes)) return null;
  if (!isStringArray(obj.libraryActionNodes)) return null;
  return {
    systemActionNodes: obj.systemActionNodes,
    libraryActionNodes: obj.libraryActionNodes,
  };
}

/**
 * Flatten the two buckets into the single de-duplicated command list the picker
 * and validator consume (system first, then library; order is otherwise
 * irrelevant — the picker sorts).
 */
export function combineActionNodeNames(names: ActionNodeNames): string[] {
  return Array.from(
    new Set([...names.systemActionNodes, ...names.libraryActionNodes])
  );
}

/**
 * 3-tier resolve for the combined list: live → cached → static default.
 * A tier is used only if it's a non-empty list, so an empty live/cache result
 * can never blank out the validator — it falls through to the next tier.
 */
export function resolveActionNodeNames(opts: {
  fetched?: string[] | null;
  cached?: string[] | null;
}): string[] {
  const { fetched, cached } = opts;
  if (fetched && fetched.length) return fetched;
  if (cached && cached.length) return cached;
  return [...STATIC_ACTION_NODE_NAMES];
}

/** Read the persisted last-known-good list. `null` when absent/corrupt/empty. */
export function readCachedActionNodeNames(
  storage: Storage | null = defaultStorage()
): string[] | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(ACTION_NODE_NAMES_CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed) && parsed.length) return parsed;
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist a non-empty list as the last-known-good. Empty lists are ignored so a
 * transient empty response never overwrites a good cached snapshot. Storage
 * failures (quota, disabled) are swallowed — caching is best-effort.
 */
export function writeCachedActionNodeNames(
  list: string[],
  storage: Storage | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    if (Array.isArray(list) && list.length) {
      storage.setItem(ACTION_NODE_NAMES_CACHE_KEY, JSON.stringify(list));
    }
  } catch {
    /* best-effort cache; ignore */
  }
}
